import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import {
  updatePlayer,
  deletePlayer,
  setPaid,
  addRebuy,
  addAddOn,
  eliminatePlayer,
  reinstatePlayer,
  togglePlayerSelection,
  selectAllPlayers,
  deselectAllPlayers,
} from '@/store/playersSlice';
import { Player } from '@/types';
import clsx from 'clsx';
import {
  User,
  DollarSign,
  RotateCcw,
  Plus,
  Trash2,
  Clock,
  CheckSquare,
  Square,
} from 'lucide-react';

interface PlayersListProps {
  className?: string;
}

export const PlayersList: React.FC<PlayersListProps> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { players, selectedPlayers, filter } = useSelector(
    (state: RootState) => state.players
  );
  const tournament = useSelector((state: RootState) => state.tournament.current);
  const currentRound = useSelector((state: RootState) => state.clock.currentRound);

  const [editingPlayer, setEditingPlayer] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Player>>({});

  // Filter players based on current filter settings
  const filteredPlayers = useMemo(() => {
    return players.filter(player => {
      if (!filter.showActive && !player.timeOut) return false;
      if (!filter.showEliminated && player.timeOut) return false;
      if (!filter.showUnpaid && !player.paid) return false;
      return true;
    });
  }, [players, filter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const active = players.filter(p => !p.timeOut).length;
    const eliminated = players.filter(p => p.timeOut).length;
    const unpaid = players.filter(p => !p.paid).length;
    const totalRebuys = players.reduce((sum, p) => sum + p.rebuys, 0);
    const totalAddOns = players.reduce((sum, p) => sum + p.addOns, 0);

    return {
      total: players.length,
      active,
      eliminated,
      unpaid,
      totalRebuys,
      totalAddOns,
    };
  }, [players]);

  const handleStartEdit = (player: Player) => {
    setEditingPlayer(player.id);
    setEditForm({
      name: player.name,
      table: player.table,
      seat: player.seat,
      chips: player.chips,
    });
  };

  const handleSaveEdit = () => {
    if (editingPlayer && editForm.name?.trim()) {
      dispatch(updatePlayer({
        id: editingPlayer,
        updates: editForm
      }));
      setEditingPlayer(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
    setEditForm({});
  };

  const handleTogglePaid = (playerId: string, currentPaid: boolean) => {
    dispatch(setPaid({ id: playerId, paid: !currentPaid }));
  };

  const handleAddRebuy = (playerId: string) => {
    dispatch(addRebuy(playerId));
  };

  const handleAddAddOn = (playerId: string) => {
    dispatch(addAddOn(playerId));
  };

  const handleEliminatePlayer = (playerId: string) => {
    dispatch(eliminatePlayer({ id: playerId, round: currentRound }));
  };

  const handleReinstatePlayer = (playerId: string) => {
    dispatch(reinstatePlayer(playerId));
  };

  const handleDeletePlayer = (playerId: string) => {
    if (confirm('Are you sure you want to delete this player?')) {
      dispatch(deletePlayer(playerId));
    }
  };

  const handleSelectAll = () => {
    if (selectedPlayers.length === filteredPlayers.length) {
      dispatch(deselectAllPlayers());
    } else {
      dispatch(selectAllPlayers());
    }
  };

  const getRankText = (player: Player) => {
    if (player.timeOut && player.rank > 0) {
      const suffix = player.rank === 1 ? 'st' : 
                    player.rank === 2 ? 'nd' : 
                    player.rank === 3 ? 'rd' : 'th';
      return `${player.rank}${suffix}`;
    }
    return '';
  };

  const canRebuy = tournament?.settings.allowRebuys && 
                  currentRound <= tournament.settings.disableRebuysAfterRound;
  const canAddOn = tournament?.settings.allowAddOns && 
                   currentRound <= tournament.settings.disableAddOnsAfterRound;

  return (
    <div className={clsx('bg-white rounded-lg shadow-lg', className)}>
      {/* Header with Statistics */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <User size={24} />
            Players ({stats.total})
          </h2>
          
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
          >
            {selectedPlayers.length === filteredPlayers.length ? (
              <CheckSquare size={16} />
            ) : (
              <Square size={16} />
            )}
            Select All
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div className="bg-green-100 px-3 py-2 rounded">
            <div className="text-green-600 font-medium">Active</div>
            <div className="text-green-800 font-bold">{stats.active}</div>
          </div>
          <div className="bg-red-100 px-3 py-2 rounded">
            <div className="text-red-600 font-medium">Eliminated</div>
            <div className="text-red-800 font-bold">{stats.eliminated}</div>
          </div>
          <div className="bg-yellow-100 px-3 py-2 rounded">
            <div className="text-yellow-600 font-medium">Unpaid</div>
            <div className="text-yellow-800 font-bold">{stats.unpaid}</div>
          </div>
          <div className="bg-blue-100 px-3 py-2 rounded">
            <div className="text-blue-600 font-medium">Rebuys</div>
            <div className="text-blue-800 font-bold">{stats.totalRebuys}</div>
          </div>
          <div className="bg-purple-100 px-3 py-2 rounded">
            <div className="text-purple-600 font-medium">Add-ons</div>
            <div className="text-purple-800 font-bold">{stats.totalAddOns}</div>
          </div>
          <div className="bg-gray-100 px-3 py-2 rounded">
            <div className="text-gray-600 font-medium">Selected</div>
            <div className="text-gray-800 font-bold">{selectedPlayers.length}</div>
          </div>
        </div>
      </div>

      {/* Players Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Select
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Table/Seat
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Paid
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rebuys
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Add-ons
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Chips
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPlayers.map((player) => (
              <tr
                key={player.id}
                className={clsx(
                  'hover:bg-gray-50 transition-colors',
                  {
                    'bg-blue-50': selectedPlayers.includes(player.id),
                    'opacity-60': player.timeOut,
                  }
                )}
              >
                {/* Select */}
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedPlayers.includes(player.id)}
                    onChange={() => dispatch(togglePlayerSelection(player.id))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>

                {/* Name */}
                <td className="px-4 py-3">
                  {editingPlayer === player.id ? (
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      autoFocus
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{player.name}</span>
                      {player.timeOut && (
                        <span className="text-xs text-red-600 font-medium">
                          {getRankText(player)}
                        </span>
                      )}
                    </div>
                  )}
                </td>

                {/* Table/Seat */}
                <td className="px-4 py-3">
                  {editingPlayer === player.id ? (
                    <div className="flex gap-1">
                      <input
                        type="number"
                        value={editForm.table || ''}
                        onChange={(e) => setEditForm({ ...editForm, table: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                        placeholder="Table"
                        min="0"
                      />
                      <input
                        type="number"
                        value={editForm.seat || ''}
                        onChange={(e) => setEditForm({ ...editForm, seat: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                        placeholder="Seat"
                        min="0"
                      />
                    </div>
                  ) : (
                    <span className="text-gray-600">
                      {player.table > 0 ? `${player.table}/${player.seat}` : 'Not assigned'}
                    </span>
                  )}
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <span className={clsx(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    {
                      'bg-green-100 text-green-800': !player.timeOut,
                      'bg-red-100 text-red-800': player.timeOut,
                    }
                  )}>
                    {player.timeOut ? 'Eliminated' : 'Active'}
                  </span>
                </td>

                {/* Paid */}
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleTogglePaid(player.id, player.paid)}
                    className={clsx(
                      'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors',
                      {
                        'bg-green-100 text-green-800 hover:bg-green-200': player.paid,
                        'bg-red-100 text-red-800 hover:bg-red-200': !player.paid,
                      }
                    )}
                  >
                    <DollarSign size={12} />
                    {player.paid ? 'Paid' : 'Unpaid'}
                  </button>
                </td>

                {/* Rebuys */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-900 font-medium">{player.rebuys}</span>
                    {canRebuy && !player.timeOut && (
                      <button
                        onClick={() => handleAddRebuy(player.id)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Add rebuy"
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                </td>

                {/* Add-ons */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-900 font-medium">{player.addOns}</span>
                    {canAddOn && !player.timeOut && (
                      <button
                        onClick={() => handleAddAddOn(player.id)}
                        className="text-purple-600 hover:text-purple-800"
                        title="Add add-on"
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                </td>

                {/* Chips */}
                <td className="px-4 py-3">
                  {editingPlayer === player.id ? (
                    <input
                      type="number"
                      value={editForm.chips || ''}
                      onChange={(e) => setEditForm({ ...editForm, chips: parseInt(e.target.value) || 0 })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                      min="0"
                    />
                  ) : (
                    <span className="text-gray-900 font-medium">
                      {player.chips.toLocaleString()}
                    </span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {editingPlayer === player.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="text-green-600 hover:text-green-800 px-1"
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-600 hover:text-red-800 px-1"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleStartEdit(player)}
                          className="text-blue-600 hover:text-blue-800 px-1"
                          title="Edit"
                        >
                          ✎
                        </button>
                        
                        {player.timeOut ? (
                          <button
                            onClick={() => handleReinstatePlayer(player.id)}
                            className="text-green-600 hover:text-green-800 px-1"
                            title="Reinstate"
                          >
                            <RotateCcw size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleEliminatePlayer(player.id)}
                            className="text-orange-600 hover:text-orange-800 px-1"
                            title="Eliminate"
                          >
                            <Clock size={14} />
                          </button>
                        )}

                        <button
                          onClick={() => handleDeletePlayer(player.id)}
                          className="text-red-600 hover:text-red-800 px-1"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredPlayers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No players found matching the current filter.
          </div>
        )}
      </div>
    </div>
  );
};